import FlagIconComponent from '@components/icons/flagIcon';
import { reportModalMachine } from '@machines/reportModalStateMachine';
import { useMachine } from '@xstate/react';
import React, { useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { Heading2, Paragraph2 } from '@components/web/texts/Texts';
import PrimaryButtonComponent from '@components/buttons/PrimaryButton';
import LoadingIconBigComponent from '@components/icons/loadingIconBig';
import Image from 'next/image';

import MascotIntro from '/public/images/illustrations/mascotIntro.png';
import { useUser } from 'next-firebase-auth';
import { UserReportOption } from '@requests/userReportsRequest';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DefaultTooltip } from '@utils/tooltip-utils';
import { useUserReportLessonFiles } from '@hooks/useUserReportLessonFiles';
import { regular_xs } from '@components/web/texts/Styles';
import { SpacerComponent } from '@components/Spacer';
import { TOOLTIP_DELAY_SHOW } from '@utils/constants';

function UserReportComponent({
  tutorialId,
  lessonId,
  tutorialVersion,
}: {
  tutorialId: number;
  lessonId: number;
  tutorialVersion: number;
}) {
  const user = useUser();
  const [reportState, sendReportStateEvent] = useMachine(reportModalMachine);
  const { userReportLessonFiles } = useUserReportLessonFiles();

  useEffect(() => {
    sendReportStateEvent({
      type: 'set initial context',
      user,
      tutorialId,
      lessonId,
      tutorialVersion,
    });
  }, [user.id, lessonId]);

  const shouldSendFilesWithReport =
    (reportState.context.reason === 'content_wrong_answer' ||
      reportState.context.reason === 'app_issue') &&
    userReportLessonFiles.length > 0;

  function dialogContent() {
    switch (true) {
      case reportState.matches('Choose Reason'):
        return (
          <div className="flex w-full flex-col items-center justify-center space-y-6 rounded-xl bg-product-background-secondary-dark p-10">
            <Heading2 className="text-product-text-primary-dark">
              Report an issue
            </Heading2>
            <div className="h-[180px] w-full rounded-xl bg-product-background-primary-dark px-4 py-6">
              {UserReportSelectReasonComponent(
                reportState.context.reason,
                (value) => {
                  sendReportStateEvent({
                    type: 'select reason',
                    reason: value,
                  });
                },
              )}
            </div>
            <SentUserCodeNotice
              shouldSendFilesWithReport={shouldSendFilesWithReport}
            ></SentUserCodeNotice>
            <PrimaryButtonComponent
              className="w-full"
              onClick={() =>
                sendReportStateEvent({ type: 'continue with reason' })
              }
            >
              Continue
            </PrimaryButtonComponent>
          </div>
        );
      case reportState.matches('Accepting Message Input'):
        return (
          <div className="flex w-full flex-col items-center justify-center space-y-6 rounded-xl bg-product-background-secondary-dark p-10">
            <Heading2 className="text-product-text-primary-dark">
              Report an issue
            </Heading2>
            <div className="h-[180px] w-full rounded-xl">
              <textarea
                onChange={(e) =>
                  sendReportStateEvent({
                    type: 'input message',
                    message: e.target.value,
                  })
                }
                placeholder="Please explain your issue in a little bit more detail"
                className="h-[180px] w-full resize-none rounded-xl bg-product-background-primary-dark  p-4 text-product-text-primary-dark placeholder:text-product-text-disabled-dark"
              ></textarea>
            </div>
            <SentUserCodeNotice
              shouldSendFilesWithReport={shouldSendFilesWithReport}
            ></SentUserCodeNotice>
            <PrimaryButtonComponent
              className="w-full"
              onClick={() =>
                sendReportStateEvent({
                  type: 'submit report',
                  userCode: shouldSendFilesWithReport
                    ? userReportLessonFiles
                    : undefined,
                })
              }
              disabled={reportState.context.message.length === 0}
            >
              Report
            </PrimaryButtonComponent>
          </div>
        );
      case reportState.matches('Submitting Report'):
        return (
          <div className="flex w-full flex-col items-center justify-center space-y-6 rounded-xl  bg-product-background-secondary-dark p-10">
            <Heading2 className="text-product-text-primary-dark">
              Report an issue
            </Heading2>
            <div className="flex h-[180px] w-full items-center justify-center rounded-xl">
              <LoadingIconBigComponent className="text-product-text-primary-dark" />
            </div>
            <div className="h-10"></div>
          </div>
        );
      case reportState.matches('Report sent'):
        return (
          <div className="flex w-full flex-col items-center justify-center space-y-6 rounded-xl bg-product-background-secondary-dark p-10 ">
            <div className="flex h-[180px] w-full items-center justify-center rounded-xl">
              <Image
                alt={'Mascot Intro'}
                src={MascotIntro}
                className="h-[210px] w-[192px]"
              ></Image>
            </div>
            <Heading2 className="text-product-text-primary-dark">
              Report sent
            </Heading2>
            <Paragraph2 className="text-text-secondary-dark">
              Thanks for improving Mimo for everyone!
            </Paragraph2>
            <Dialog.Close asChild>
              <PrimaryButtonComponent className="w-full">
                Done
              </PrimaryButtonComponent>
            </Dialog.Close>
          </div>
        );

      default:
        return <></>;
    }
  }

  function didCloseDialog() {
    sendReportStateEvent({ type: 'reset' });
  }

  return (
    // NOTE: wrapper div is required otherwise react-tooltip has placement issues with radix-ui dialog
    <div>
      <ReactTooltip
        className="defaultTooltip"
        arrowColor="transparent"
        id="user-dropdown-tooltip"
        opacity={1}
        delayShow={TOOLTIP_DELAY_SHOW}
      ></ReactTooltip>
      <Dialog.Root onOpenChange={(isOpen) => (isOpen ? '' : didCloseDialog())}>
        <Dialog.Trigger
          className="flex h-8 w-8 items-center justify-center rounded-lg text-product-button-text-enabled-dark hover:bg-product2-background-tertiary"
          data-tooltip-id="user-dropdown-tooltip"
          data-tooltip-html={DefaultTooltip('Report lesson')}
        >
          <FlagIconComponent className="h-5 w-5 text-icon-primary-dark"></FlagIconComponent>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-black/60" />
          <Dialog.Content className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-50 max-h-[85vh] w-[90vw] max-w-[450px] translate-x-[-50%] translate-y-[-50%] rounded-xl focus:outline-none">
            {dialogContent()}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function SentUserCodeNotice({
  shouldSendFilesWithReport,
}: {
  shouldSendFilesWithReport: boolean;
}) {
  return (
    <p
      className={`${regular_xs} !mt-1 w-full text-product2-content-secondary ${
        shouldSendFilesWithReport ? '' : 'invisible'
      }`}
    >
      Your code will be sent with the report automatically.
    </p>
  );
}

function UserReportSelectReasonComponent(
  reason: UserReportOption,
  onValueChange: (value: UserReportOption) => void,
) {
  return (
    <form>
      <RadioGroup.Root
        onValueChange={onValueChange}
        className="flex flex-col space-y-4"
        defaultValue="content_typo"
        aria-label="View density"
      >
        <ReasonRadioButtonComponent
          isSelected={reason === 'content_typo'}
          htmlFor="r1"
          label="I caught a typo."
          value="content_typo"
        />
        <ReasonRadioButtonComponent
          isSelected={reason === 'content_wrong_answer'}
          htmlFor="r2"
          label="The answer is wrong."
          value="content_wrong_answer"
        />
        <ReasonRadioButtonComponent
          isSelected={reason === 'content_confusing'}
          htmlFor="r3"
          label="The question or hints are confusing."
          value="content_confusing"
        />
        <ReasonRadioButtonComponent
          isSelected={reason === 'app_issue'}
          htmlFor="r4"
          label="Something isn't working."
          value="app_issue"
        />
      </RadioGroup.Root>
    </form>
  );
}
function ReasonRadioButtonComponent({
  isSelected,
  value,
  label,
  htmlFor,
}: {
  isSelected: boolean;
  value: UserReportOption;
  label: string;
  htmlFor: string;
}) {
  return (
    <div className="flex cursor-pointer items-center">
      <RadioGroup.Item
        className={`h-[20px] w-[20px] rounded-full border-2 outline-none hover:border-product-radioButton-hover-dark  ${
          isSelected
            ? 'border-product-radioButton-selected-dark'
            : 'border-product-radioButton-enabled-dark'
        }`}
        value={value}
        id={htmlFor}
      >
        <RadioGroup.Indicator className="relative flex h-full w-full items-center justify-center after:block after:h-[12px] after:w-[12px] after:rounded-[50%] after:bg-product-radioButton-selected-light after:content-[''] dark:after:bg-product-radioButton-selected-dark" />
      </RadioGroup.Item>
      <label
        className="cursor-pointer pl-[15px] text-[15px] leading-none text-text-primary-dark"
        htmlFor={htmlFor}
      >
        {label}
      </label>
    </div>
  );
}

export default UserReportComponent;
